<template>
  <tr :class="rowClasses" @click="handleRowClick">
    <slot v-bind="slotProps" />
  </tr>
</template>

<script>
import { computed } from 'vue'

import { getFileFlags } from '@/utils/fileFlags'

// Elements that should NOT trigger row selection on click: interactive
// controls (links, buttons, inputs…) plus value cells (`.table-cell`), so
// users can click/double-click to select and copy their text. Rows are still
// selected via the checkbox column, the icon columns, or drag.
const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, [role="button"], .table-cell'

export default {
  name: 'DataTableRow',
  props: {
    record: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggle-select'],
  setup(props, { emit }) {
    // ===========================================
    // Deleted record helpers
    // ===========================================
    const isDeleted = computed(() => props.record.deleted?.value === true)

    const deletedTooltip = computed(() => {
      if (!isDeleted.value) return null
      const username = props.record.deleted_by?.value?.username
      const date = props.record.deleted_date?.value
      const byText = username ? `@${username}` : 'someone'
      const dateText = date ? `on ${date}` : 'at an unknown date'
      return `This was deleted by ${byText} ${dateText}`
    })

    // ===========================================
    // Status helpers
    // ===========================================
    const status = computed(
      () =>
        props.record.sample_status?.value ||
        props.record.status?.value ||
        props.record.state?.value,
    )

    // ===========================================
    // File preview/download helpers
    // ===========================================
    // Reuse the flags ListComponent already stored on __meta__; fall back to
    // computing them for records that weren't processed there.
    const fileFlags = computed(
      () => props.record.__meta__ ?? getFileFlags(props.record),
    )

    // ===========================================
    // Row classes and slot props
    // ===========================================
    const rowClasses = computed(() => ({
      'is-selected': props.selected,
      'is-deleted-record': isDeleted.value,
    }))

    const slotProps = computed(() => ({
      record: props.record,
      isDeleted: isDeleted.value,
      deletedTooltip: deletedTooltip.value,
      status: status.value,
      viewable: fileFlags.value.viewable,
      downloadable: fileFlags.value.downloadable,
      contentType: fileFlags.value.contentType,
      filesize: fileFlags.value.filesize,
      recordId: props.record.id?.value,
    }))

    const handleRowClick = (event) => {
      // Don't toggle when clicking interactive elements (links, buttons, inputs…)
      if (event.target.closest(INTERACTIVE_SELECTOR)) return
      emit('toggle-select', event)
    }

    return { rowClasses, slotProps, handleRowClick }
  },
}
</script>
